import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Rating,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import axios from "../../axios";
import usePageTitle from "../pageTitle";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import CheckoutFooter from "../components/footer";

// Material Dashboard 2 React example components
// import { useAppLocation } from "agent_location_context";
import { useAwaitDialog } from "../components/status_dialog";
import DataFormComponent from "../components/data_forms/data_form";

function PSAFeedbackPage() {
  const pageTitle = `Product Sampling - Feedback`;
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const statusDialog = useAwaitDialog();
  const dataFormRef = useRef();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [resultData, setResultData] = useState({});
  const [productsFeedback, setProductsFeedback] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`web/psa/${ref}/feedback/init.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setResultData(result.data.data);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  function submitFeedback() {
    // Validate ratings
    for (var i = 0; i < resultData.products.length; i++) {
      var p = resultData.products[i];
      var pf = productsFeedback[p.reference];

      if (!pf || !pf.rating || pf.rating <= 0) {
        setSeverity("error");
        setErrMsg("Please rate all sampled products.");
        setOpen(true);
        return;
      }
    }

    // Get form data
    let formValues = null;
    if (resultData.data_form !== null) {
      const formResult = dataFormRef.current.submit();
      if (formResult.error) {
        setSeverity("error");
        setErrMsg(formResult.error);
        setOpen(true);
        return;
      }
      formValues = formResult.values;
    }

    var products = resultData.products.map(function (p) {
      var pf = productsFeedback[p.reference] || {};

      return {
        reference: p.reference,
        rating: pf.rating || 0,
        would_buy: pf.would_buy || "",
        would_recommend: pf.would_recommend || "",
        comment: pf.comment || "",
      };
    });

    var payload = {
      reference: ref,
      products: products,
      data_form: formValues,
    };

    setShowLoading(true);
    axios
      .post("web/psa/feedback/submit.json", payload)
      .then(async function (result) {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success");
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async function () {
        setShowLoading(false);
        await statusDialog.show(
          "Unable to complete request.",
          "error",
          "Failed",
        );
      });
  }

  return (
    <Box>
      <Box mb={5}>
        <Container>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={showLoading}
            onClick={handleClose}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <Snackbar
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleClose}
              severity={severity}
              sx={{ width: "100%" }}
            >
              {errorMsg}
            </Alert>
          </Snackbar>
          <Box mt={1} mb={5}>
            {pageLoaded && (
              <Box sx={{ margin: "35px auto", maxWidth: "600px" }}>
                <Box>
                  <Card>
                    <Box p={2}>
                      <Box align="center">
                        <Typography variant="caption" color="text">
                          Campaign
                        </Typography>
                        <Typography variant="h5">
                          {resultData.campaign}
                        </Typography>
                      </Box>

                      <Box>
                        <Divider />
                      </Box>

                      <Box mt={3}>
                        {resultData.products.map((product, idx) => (
                          <Box mb={3} key={product.reference}>
                            <Typography
                              variant="h6"
                              fontWeight="regular"
                              fontSize={16}
                            >
                              {++idx}. {product.title}
                            </Typography>
                            <Box mt={1.5} mb={2}>
                              <Typography component="legend" fontSize={14}>
                                Rate
                              </Typography>
                              <Rating
                                name={`rating_${product.reference}`}
                                value={
                                  productsFeedback[product.reference]?.rating ||
                                  0
                                }
                                onChange={(event, newValue) => {
                                  setProductsFeedback((prev) => ({
                                    ...prev,
                                    [product.reference]: {
                                      ...prev[product.reference],
                                      rating: newValue,
                                    },
                                  }));
                                }}
                              />
                            </Box>
                            <Box mt={2}>
                              <Typography fontSize={14}>
                                Would you buy?
                              </Typography>
                              <RadioGroup
                                row
                                value={
                                  productsFeedback[product.reference]
                                    ?.would_buy ?? ""
                                }
                                onChange={(e) =>
                                  setProductsFeedback((prev) => ({
                                    ...prev,
                                    [product.reference]: {
                                      ...prev[product.reference],
                                      would_buy: e.target.value,
                                    },
                                  }))
                                }
                                orientation="vertical"
                              >
                                <FormControlLabel
                                  value="yes"
                                  control={<Radio />}
                                  label="Yes"
                                />
                                <FormControlLabel
                                  value="maybe"
                                  control={<Radio />}
                                  label="Maybe"
                                />
                                <FormControlLabel
                                  value="no"
                                  control={<Radio />}
                                  label="No"
                                />
                              </RadioGroup>
                            </Box>
                            <Box mt={2}>
                              <Typography fontSize={14}>
                                Would you recommend?
                              </Typography>
                              <RadioGroup
                                row
                                value={
                                  productsFeedback[product.reference]
                                    ?.would_recommend ?? ""
                                }
                                onChange={(e) =>
                                  setProductsFeedback((prev) => ({
                                    ...prev,
                                    [product.reference]: {
                                      ...prev[product.reference],
                                      would_recommend: e.target.value,
                                    },
                                  }))
                                }
                                orientation="vertical"
                              >
                                <FormControlLabel
                                  value="yes"
                                  control={<Radio />}
                                  label="Yes"
                                />
                                <FormControlLabel
                                  value="maybe"
                                  control={<Radio />}
                                  label="Maybe"
                                />
                                <FormControlLabel
                                  value="no"
                                  control={<Radio />}
                                  label="No"
                                />
                              </RadioGroup>
                            </Box>
                            <Box mt={2}>
                              <Typography fontSize={14} mb={1}>
                                Leave any comments (optional)
                              </Typography>
                              <TextField
                                label="Comments (Optional)"
                                type="text"
                                multiline
                                minRows={2}
                                fullWidth
                                value={
                                  productsFeedback[product.reference]
                                    ?.comment || ""
                                }
                                onChange={(e) =>
                                  setProductsFeedback((prev) => ({
                                    ...prev,
                                    [product.reference]: {
                                      ...prev[product.reference],
                                      comment: e.target.value,
                                    },
                                  }))
                                }
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Card>
                </Box>
                {resultData.data_form && (
                  <Box mt={3}>
                    <DataFormComponent
                      ref={dataFormRef}
                      dataForm={resultData.data_form}
                    />
                  </Box>
                )}
                <Box mt={5} mb={1}>
                  <Card>
                    <Box p={3}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={submitFeedback}
                        fullWidth
                      >
                        Submit Feedback
                      </Button>
                    </Box>
                  </Card>
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
      <CheckoutFooter />
    </Box>
  );
}

export default PSAFeedbackPage;
